import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styles from './BottomNav.module.css';
import { useAuth } from 'src/hooks/useAuth';
import navConfig from './nav/config';

const BottomNav = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const { data, dispatch } = useAuth();
  const navigate = useNavigate();

  // Get nav items from config (same as desktop nav)
  const navItems = navConfig || [];

  // Filter out 'data insight' and 'administration' from nav items for mobile
  const filteredNavItems = navItems.filter(item => {
    const title = item.title.toLowerCase();
    return title !== 'data insight' && title !== 'administration';
  });

  // Define which nav items to show in bottom nav for each user type
  const getBottomNavItems = () => {
    if (data.merchant_type === 'shop') {
      return ['dashboard', 'orders', 'sales', 'products'];
    } else if (data.merchant_type === 'brand') {
      return ['dashboard', 'sales', 'Stores', 'products'];
    } else {
      return ['dashboard', 'sales', 'products'];
    }
  };

  const allowedTitles = getBottomNavItems();
  const mainNavItems = filteredNavItems.filter(item => 
    allowedTitles.includes(item.title) || 
    allowedTitles.includes(item.title.toLowerCase())
  );

  // Only show the first 4 items in the bottom nav
  const bottomNavItems = mainNavItems.slice(0, 4);
  // The rest go into the modal
  const modalNavItems = filteredNavItems.filter(
    item => !bottomNavItems.some(b => b.title === item.title)
  );

  useEffect(() => {
    let timeoutId;

    const handleActivity = () => {
      setIsVisible(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };

    // Initial setup
    handleActivity();

    // Add event listeners
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('scroll', handleActivity, true); // Use capture phase for scroll

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('scroll', handleActivity, true);
    };
  }, []);

  // Logout handler
  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    dispatch({ type: 'LOGIN', payload: {} });
    setShowModal(false);
    setTimeout(() => {
      navigate('/login');
    }, 100);
  };

  return (
    <>
      <nav className={`${styles.bottomNav} ${isVisible ? styles.visible : styles.hidden}`}>
        {bottomNavItems.map((item) => (
          <NavLink
            key={item.title}
            to={item.path}
            className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
          >
            <img src={item.icon} alt={item.title} />
            <span>{item.title.charAt(0).toUpperCase() + item.title.slice(1)}</span>
          </NavLink>
        ))}
        <button
          className={styles.navLink}
          onClick={() => setShowModal(true)}
          style={{ background: 'none', border: 'none', outline: 'none' }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            fill="currentColor"
          >
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
          <span>More</span>
        </button>
      </nav>
      {showModal && (
        <>
          <div className={styles.moreCloseArea} onClick={() => setShowModal(false)} />
          <div className={styles.morePopover}>
            <ul className={styles.moreNavList}>
              {modalNavItems.map(item => (
                <li key={item.title}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `${styles.moreNavItem} ${isActive ? styles.active : ''}`}
                    onClick={() => setShowModal(false)}
                  >
                    <img src={item.icon} alt={item.title} />
                    <span>{item.title.charAt(0).toUpperCase() + item.title.slice(1)}</span>
                  </NavLink>
                </li>
              ))}
              <li>
                <NavLink
                  to="/app/settings"
                  className={({ isActive }) => `${styles.moreNavItem} ${isActive ? styles.active : ''}`}
                  onClick={() => setShowModal(false)}
                >
                  <img src="/assets/icons/settings.svg" alt="Settings" />
                  <span>Settings</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/app/profile"
                  className={({ isActive }) => `${styles.moreNavItem} ${styles.profileLink} ${isActive ? styles.active : ''}`}
                  onClick={() => setShowModal(false)}
                >
                  <img src="/assets/icons/profile.svg" alt="Store Profile" />
                  <span>Store Profile</span>
                </NavLink>
              </li>
              <li>
                <button className={`${styles.moreNavItem} ${styles.logoutButton}`} onClick={handleLogout} style={{width: '100%'}}>
                  <img src="/assets/icons/log-out.svg" alt="Logout" />
                  <span>Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </>
      )}
    </>
  );
};

export default BottomNav;

// Add these styles to BottomNav.module.css:
// .modalOverlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; z-index: 9999; }
// .modalContent { background: #fff; border-radius: 8px; padding: 24px; min-width: 250px; max-width: 90vw; }
// .modalNavList { list-style: none; padding: 0; margin: 0; } 